import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollReveal from '@/components/base/ScrollReveal';
import JsonLd from '@/components/base/JsonLd';

const categories = ['all', 'fitness', 'nutrition', 'wellness', 'training'] as const;
type Category = (typeof categories)[number];

const articlesData = [
  { id: 1, category: 'fitness' as Category, titleKey: 'blog.article1.title', excerptKey: 'blog.article1.excerpt', author: 'Ahmed Hassan', date: '2026-06-15', readTime: '5 min' },
  { id: 2, category: 'nutrition' as Category, titleKey: 'blog.article2.title', excerptKey: 'blog.article2.excerpt', author: 'Sara Mohamed', date: '2026-06-12', readTime: '7 min' },
  { id: 3, category: 'wellness' as Category, titleKey: 'blog.article3.title', excerptKey: 'blog.article3.excerpt', author: 'Fatima Ali', date: '2026-06-10', readTime: '6 min' },
  { id: 4, category: 'training' as Category, titleKey: 'blog.article4.title', excerptKey: 'blog.article4.excerpt', author: 'Omar Khaled', date: '2026-06-08', readTime: '8 min' },
  { id: 5, category: 'fitness' as Category, titleKey: 'blog.article5.title', excerptKey: 'blog.article5.excerpt', author: 'Layla Ibrahim', date: '2026-06-05', readTime: '5 min' },
  { id: 6, category: 'nutrition' as Category, titleKey: 'blog.article6.title', excerptKey: 'blog.article6.excerpt', author: 'Youssef Ahmed', date: '2026-06-03', readTime: '6 min' },
  { id: 7, category: 'wellness' as Category, titleKey: 'blog.article7.title', excerptKey: 'blog.article7.excerpt', author: 'Nour Hassan', date: '2026-06-01', readTime: '7 min' },
  { id: 8, category: 'training' as Category, titleKey: 'blog.article8.title', excerptKey: 'blog.article8.excerpt', author: 'Karim Mahmoud', date: '2026-05-28', readTime: '9 min' },
  { id: 9, category: 'fitness' as Category, titleKey: 'blog.article9.title', excerptKey: 'blog.article9.excerpt', author: 'Mona Saeed', date: '2026-05-25', readTime: '6 min' },
];

const heroImg = 'https://readdy.ai/api/search-image?query=Abstract%20artistic%20geometric%20background%20with%20warm%20coral%20and%20terracotta%20gradients%20flowing%20organic%20shapes%20minimalist%20modern%20design%20soft%20atmospheric%20light%20clean%20elegant%20composition%20editorial%20quality%20high%20detail%20deep%20warm%20tones%20no%20text%20no%20people&width=1800&height=900&seq=blog-hero-v3&orientation=landscape';

const articleImages: string[] = [
  'https://readdy.ai/api/search-image?query=Modern%20fitness%20gym%20training%20scene%20professional%20athlete%20performing%20strength%20exercises%20bright%20clean%20spacious%20facility%20minimalist%20design%20soft%20natural%20lighting%20coral%20terracotta%20warm%20accents%20editorial%20quality%20high%20detail%20no%20text&width=800&height=560&seq=blog-full-01&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Healthy%20nutrition%20meal%20preparation%20fresh%20colorful%20vegetables%20fruits%20lean%20proteins%20arranged%20beautifully%20on%20clean%20white%20surface%20natural%20lighting%20minimalist%20food%20photography%20coral%20terracotta%20warm%20tones%20editorial%20quality&width=800&height=560&seq=blog-full-02&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Peaceful%20meditation%20wellness%20scene%20person%20practicing%20mindfulness%20in%20serene%20environment%20soft%20natural%20light%20minimalist%20zen%20aesthetic%20calming%20atmosphere%20warm%20coral%20terracotta%20subtle%20accents%20editorial%20photography&width=800&height=560&seq=blog-full-03&orientation=landscape',
  'https://readdy.ai/api/search-image?query=High%20intensity%20interval%20training%20HIIT%20workout%20athletic%20person%20performing%20dynamic%20exercises%20modern%20gym%20clean%20background%20professional%20fitness%20photography%20coral%20terracotta%20warm%20tones%20editorial%20quality&width=800&height=560&seq=blog-full-04&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Comfortable%20home%20fitness%20workout%20setup%20person%20exercising%20in%20cozy%20modern%20interior%20with%20minimal%20equipment%20natural%20light%20clean%20Scandinavian%20design%20warm%20coral%20terracotta%20accents%20editorial%20photography&width=800&height=560&seq=blog-full-05&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Protein%20rich%20foods%20artistic%20display%20various%20healthy%20protein%20sources%20lean%20meats%20fish%20eggs%20legumes%20arranged%20on%20clean%20surface%20natural%20lighting%20minimalist%20style%20warm%20coral%20terracotta%20tones%20editorial%20food%20photography&width=800&height=560&seq=blog-full-06&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Peaceful%20bedroom%20sleep%20environment%20comfortable%20bed%20soft%20warm%20lighting%20calming%20minimalist%20design%20rest%20and%20recovery%20serene%20atmosphere%20coral%20terracotta%20subtle%20warm%20tones%20editorial%20interior%20photography&width=800&height=560&seq=blog-full-07&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Strength%20training%20weightlifting%20athlete%20performing%20compound%20barbell%20exercises%20professional%20gym%20setting%20clean%20background%20dramatic%20lighting%20warm%20coral%20terracotta%20accents%20editorial%20sports%20photography&width=800&height=560&seq=blog-full-08&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Yoga%20flexibility%20training%20person%20performing%20elegant%20yoga%20poses%20bright%20airy%20studio%20natural%20light%20minimalist%20peaceful%20environment%20coral%20terracotta%20warm%20undertones%20editorial%20photography%20high%20detail&width=800&height=560&seq=blog-full-09&orientation=landscape',
];

const formSubmitUrl = 'https://readdy.ai/api/form/d918rpgbhec1he3opqg0';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://5vix.com';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '5vix',
  url: siteUrl,
  description: 'Integrated gym management system for membership, scheduling, billing, attendance, and analytics.',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+201044553254',
    contactType: 'customer support',
    availableLanguage: ['English', 'Arabic'],
  },
  sameAs: [
    'https://www.instagram.com/5vixofficial/',
    'https://www.tiktok.com/@5vixofficial',
    'https://www.youtube.com/@5vix-c3p',
    'https://www.linkedin.com/in/5vix-software-12058b363/',
  ],
};

export default function BlogPage() {
  const { t, i18n } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<Category>('all');
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);

  const pageJsonLd = [
    organizationSchema,
    {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: '5vix Fitness & Wellness Blog',
      description: 'Expert articles on fitness training, nutrition, wellness, and gym management.',
      url: `${siteUrl}/blog`,
      blogPost: articlesData.map((a) => ({
        '@type': 'BlogPosting',
        headline: t(a.titleKey),
        description: t(a.excerptKey),
        author: { '@type': 'Person', name: a.author },
        datePublished: a.date,
      })),
    },
  ];

  const filteredArticles = selectedCategory === 'all'
    ? articlesData
    : articlesData.filter((a) => a.category === selectedCategory);

  return (
    <div className="bg-background-50 min-h-screen">
      <JsonLd data={pageJsonLd} />
      <Navbar />

      {/* Hero Section */}
      <section className="relative w-full min-h-[420px] md:min-h-[500px] flex items-center overflow-hidden bg-gradient-to-br from-primary-950 via-primary-900 to-primary-800">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-primary-400/20 via-transparent to-transparent"></div>

        <div className="relative z-10 w-full px-4 md:px-6 py-20 md:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-5 leading-tight">
              {t('blog.title')}
            </h1>
            <p className="text-background-50/80 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              {t('blog.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-[72px] z-40 bg-background-50 border-b border-background-200/60">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4">
          <div className="flex flex-wrap gap-2 justify-center">
            {[
              { id: 'all' as Category, label: t('blog.allArticles') },
              { id: 'fitness' as Category, label: t('blog.fitness') },
              { id: 'nutrition' as Category, label: t('blog.nutrition') },
              { id: 'wellness' as Category, label: t('blog.wellness') },
              { id: 'training' as Category, label: t('blog.training') },
            ].map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === cat.id
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <ScrollReveal>
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article, idx) => (
                <article
                  key={article.id}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                >
                  <div className="relative h-56 w-full overflow-hidden">
                    <img
                      src={articleImages[article.id - 1]}
                      alt={t(article.titleKey)}
                      title={t(article.titleKey)}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4 rtl:left-auto rtl:right-4">
                      <span className="bg-primary-500 text-background-50 text-xs px-3 py-1 rounded-full font-medium">
                        {t(`blog.${article.category}`)}
                      </span>
                    </div>
                  </div>

                  <div className="p-5">
                    <div className="flex items-center gap-4 text-xs text-foreground-400 mb-3 flex-wrap">
                      <span className="flex items-center gap-1.5">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-calendar-line text-foreground-400"></i>
                        </div>
                        {article.date}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-time-line text-foreground-400"></i>
                        </div>
                        {article.readTime}
                      </span>
                    </div>

                    <h3 className="font-heading font-semibold text-foreground-950 text-base mb-2.5 group-hover:text-primary-600 transition-colors leading-snug">
                      {t(article.titleKey)}
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed line-clamp-3 mb-4">
                      {t(article.excerptKey)}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-background-200/60">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                          <i className="ri-user-line text-primary-600 text-xs"></i>
                        </div>
                        <span className="text-sm font-medium text-foreground-700">{article.author}</span>
                      </div>
                      <div className="w-8 h-8 flex items-center justify-center text-primary-500 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform">
                        <i className={`ri-arrow-right-line ${isRTL ? 'rotate-180' : ''}`}></i>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 bg-background-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-article-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-base">{t('helpCenter.noResults')}</p>
              </div>
            )}
          </div>
        </section>
      </ScrollReveal>

      {/* Contact Support */}
      <ScrollReveal>
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="bg-primary-500 rounded-2xl p-8 md:p-12 text-center">
              <div className="w-16 h-16 bg-background-50/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-customer-service-2-line text-3xl text-background-50"></i>
              </div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
                {t('helpCenter.stillNeedHelp')}
              </h2>
              <p className="text-background-50/85 text-base mb-8 max-w-lg mx-auto leading-relaxed">
                {t('helpCenter.contactTeam')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://wa.me/+201044553254"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="bg-background-50 text-primary-600 px-7 py-3 rounded-lg hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer font-semibold text-sm inline-flex items-center justify-center gap-2"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-whatsapp-line"></i>
                  </div>
                  {t('helpCenter.whatsappSupport')}
                </a>
                <a
                  href="tel:201044553254"
                  className="bg-background-50/10 text-background-50 border-2 border-background-50/40 px-7 py-3 rounded-lg hover:bg-background-50/20 transition-all whitespace-nowrap cursor-pointer font-semibold text-sm inline-flex items-center justify-center gap-2"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-line"></i>
                  </div>
                  {t('helpCenter.callUs')}
                </a>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* CTA Section */}
      <ScrollReveal>
        <section className="py-14 md:py-20 bg-primary-500">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-5">
              {t('cta.title')}
            </h2>
            <p className="text-background-50/85 text-base md:text-lg mb-8 leading-relaxed">
              {t('cta.description')}
            </p>
            <Link
              to="/#register"
              className="inline-block bg-background-50 text-primary-600 px-10 py-3.5 rounded-lg hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer font-semibold text-sm"
            >
              {t('cta.button')}
            </Link>
          </div>
        </section>
      </ScrollReveal>

      <Footer />
    </div>
  );
}